'use client'

import React from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Navigation, Pagination } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'

import { useGetCategories } from '@/actions/category'
import type { ICategory } from '@/types/category'

// ---------------------------------------------------------------------------
// Slug → static fallback icon map.
//
// When the admin has not uploaded an icon for a category, the component falls
// back to the matching static asset bundled with the frontend. Keys are the
// Laravel-generated slugs (Str::slug of the category name). A generic inline
// SVG is used for slugs not present in this map.
// ---------------------------------------------------------------------------

const FALLBACK_ICONS: Record<string, string> = {
  'marketing-digital': '/assets/icons/marketing-digital.svg',
  'developpement-commercial-et-entreprenariat': '/assets/icons/launching.svg',
  'reseaux-sociaux-et-contenus': '/assets/icons/social-networks-and-content.svg',
  'outils-et-productivite': '/assets/icons/tools-and-productivity.svg',
  'developpement-personnel-et-leadership':
    '/assets/icons/personal-development-and-leadership.svg',
}

// ---------------------------------------------------------------------------
// GenericFolderIcon — last-resort inline SVG for unknown categories
// ---------------------------------------------------------------------------

const GenericFolderIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#FFBC42"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
  </svg>
)

// ---------------------------------------------------------------------------
// CategoryIcon — resolves the right icon source in priority order:
//   1. Backend-uploaded icon (icon_url)
//   2. Slug-keyed static fallback (FALLBACK_ICONS)
//   3. Generic SVG
// ---------------------------------------------------------------------------

interface CategoryIconProps {
  category: ICategory
  className?: string
}

const CategoryIcon: React.FC<CategoryIconProps> = ({ category, className = 'w-16 h-16' }) => {
  // Priority 1: admin-uploaded icon — served through Next.js <Image> (backend host is
  // whitelisted in next.config.ts remotePatterns so optimisation is available).
  if (category.icon_url) {
    return (
      <Image
        src={category.icon_url}
        alt={category.name}
        width={64}
        height={64}
        className={`${className} object-contain`}
      />
    )
  }

  // Priority 2: bundled static fallback matched by slug
  const staticSrc = FALLBACK_ICONS[category.slug]
  if (staticSrc) {
    return (
      <Image
        src={staticSrc}
        alt={category.name}
        width={70}
        height={70}
        className={`${className} object-contain`}
      />
    )
  }

  // Priority 3: generic inline SVG
  return <GenericFolderIcon className={`${className} text-[#FC7F16]`} />
}

// ---------------------------------------------------------------------------
// CategoryCard — single card cell (shared between grid and Swiper)
// ---------------------------------------------------------------------------

interface CategoryCardProps {
  category: ICategory
  mobile?: boolean
}

const CategoryCard: React.FC<CategoryCardProps> = ({ category, mobile = false }) => {
  const baseClasses =
    'hover:shadow transition duration-300 hover:scale-95 hover:bg-gray-50 flex flex-col items-center justify-center border border-[#FFBC42] rounded-2xl bg-white text-center p-4'
  const mobileClasses = 'w-[182px] h-[164px]'

  return (
    <Link
      href={`/formations/${category.slug}`}
      className={mobile ? `${baseClasses} ${mobileClasses}` : baseClasses}
    >
      <CategoryIcon category={category} className="w-16 h-16" />
      <p className="pt-2.5 text-[#1B365E] text-[12px] leading-5 font-normal tracking-[0.2px]">
        {category.name}
      </p>
    </Link>
  )
}

// ---------------------------------------------------------------------------
// CategoryCardSkeleton — pulse placeholder while loading
// ---------------------------------------------------------------------------

const CategoryCardSkeleton: React.FC<{ mobile?: boolean }> = ({ mobile = false }) => {
  const mobileClasses = 'w-[182px] h-[164px]'
  const baseClasses =
    'flex flex-col items-center justify-center border border-[#FFBC42]/30 rounded-2xl bg-gray-100 animate-pulse p-4'

  return (
    <div className={mobile ? `${baseClasses} ${mobileClasses}` : baseClasses}>
      <div className="w-16 h-16 rounded-full bg-gray-200" />
      <div className="mt-3 w-20 h-3 rounded bg-gray-200" />
    </div>
  )
}

// ---------------------------------------------------------------------------
// YourAutonomyOurSuccess
// ---------------------------------------------------------------------------

const SKELETON_COUNT = 5

const YourAutonomyOurSuccess: React.FC = () => {
  const { categories, categoriesLoading, categoriesError, categoriesEmpty } =
    useGetCategories()

  return (
    <div className="mt-9.75 px-4">
      <h2 className="font-red-hat text-[30px] md:text-[40px] leading-10 md:leading-13.25 text-[#1B365E] font-light tracking-[0.3px] text-center">
        Votre autonomie, c&apos;est notre réussite !
      </h2>
      <p className="mt-3.5 text-[#FC7F16] text-[20px] leading-7.5 font-medium text-center tracking-[0.2px]">
        Les catégories de nos formations
      </p>

      <div className="relative max-w-267.5 mx-auto mt-8">
        {/* ---- Error ---- */}
        {categoriesError && (
          <p className="text-center text-red-500 py-6 text-sm">
            Une erreur est survenue lors du chargement des catégories.
          </p>
        )}

        {/* ---- Empty (after successful load) ---- */}
        {categoriesEmpty && !categoriesError && (
          <p className="text-center text-[#676767] py-6 text-sm">
            Aucune catégorie disponible pour le moment.
          </p>
        )}

        {/* ---- Carousel ---- */}
        {!categoriesError && (
          <>
            {categoriesLoading ? (
              <div className="flex gap-10 overflow-hidden pb-12">
                {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
                  <CategoryCardSkeleton key={i} mobile />
                ))}
              </div>
            ) : (
              <Swiper
                modules={[Navigation, Pagination]}
                spaceBetween={40}
                slidesPerView="auto"
                navigation={{ prevEl: '.cat-next', nextEl: '.cat-prev' }}
                pagination={{ clickable: true, el: '.custom-pagination-success' }}
                className="pb-12"
              >
                {categories.map((cat) => (
                  <SwiperSlide key={cat.id} className="w-45.5! flex justify-center">
                    <CategoryCard key={cat.id} category={cat} mobile />
                  </SwiperSlide>
                ))}
              </Swiper>
            )}

            <div className="custom-pagination-success flex justify-center gap-1.5 mt-4" />
          </>
        )}

        {/* ---- Navigation Arrows ---- */}
        <div className="hidden xl:flex flex-col gap-5 absolute -right-10 top-3 z-20">
          <button className="cat-next cursor-pointer transition-all hover:scale-110">
            <svg width="28" height="30" viewBox="0 0 28 30" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M27.4142 16.1408C28.1953 15.3597 28.1953 14.0934 27.4142 13.3123L14.6863 0.584427C13.9052 -0.196622 12.6389 -0.196622 11.8579 0.584427C11.0768 1.36548 11.0768 2.63181 11.8579 3.41285L23.1716 14.7266L11.8579 26.0403C11.0768 26.8213 11.0768 28.0876 11.8579 28.8687C12.6389 29.6497 13.9052 29.6497 14.6863 28.8687L27.4142 16.1408ZM0 16.7266H26V12.7266H0V16.7266Z" fill="currentColor" />
            </svg>
          </button>
          <button className="cat-prev cursor-pointer transition-all hover:scale-110">
            <svg width="28" height="30" viewBox="0 0 28 30" fill="none" xmlns="http://www.w3.org/2000/svg" className="rotate-180">
              <path d="M27.4142 16.1408C28.1953 15.3597 28.1953 14.0934 27.4142 13.3123L14.6863 0.584427C13.9052 -0.196622 12.6389 -0.196622 11.8579 0.584427C11.0768 1.36548 11.0768 2.63181 11.8579 3.41285L23.1716 14.7266L11.8579 26.0403C11.0768 26.8213 11.0768 28.0876 11.8579 28.8687C12.6389 29.6497 13.9052 29.6497 14.6863 28.8687L27.4142 16.1408ZM0 16.7266H26V12.7266H0V16.7266Z" fill="currentColor" />
            </svg>
          </button>
        </div>
      </div>

      <style jsx global>{`
        .custom-pagination-success .swiper-pagination-bullet {
          width: 6px;
          height: 6px;
          background: #d9d9d9;
          opacity: 1;
        }
        .custom-pagination-success .swiper-pagination-bullet-active {
          background: #40bfde;
        }
        .cat-next,
        .cat-prev {
          color: #1b365e;
        }
        .cat-next.swiper-button-disabled,
        .cat-prev.swiper-button-disabled {
          color: #ababab;
          opacity: 1;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  )
}

export default YourAutonomyOurSuccess